import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Dropdown, DropdownButton, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Main from "../../redux/actions/main.action";
import { BaseState, StateKeys } from "../../redux/states/main.state";
import ApiEndpoint from "../../services/api.endpoint.service";
import {
  ClassContentDiscussionResponse,
  DiscussionCommentResponse,
} from "../../services/api.response.service";
import { IRootState } from "../../redux/states";
import { AppState } from "../../services/app.service";
import moment from "moment";

interface props {
  discussion: ClassContentDiscussionResponse;
  setDiscussion: Dispatch<
    SetStateAction<ClassContentDiscussionResponse | undefined>
  >;
  contentID: string | number;
}

const DiscussionReplies = ({ discussion, setDiscussion, contentID }: props) => {
  const { user } = useSelector((state: IRootState) => state.setting);
  const dispatch = useDispatch();
  const state = useSelector((state: IRootState) => state.main);
  const comments: BaseState<Array<DiscussionCommentResponse>> =
    state[StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS];
  const [pageComments, setPageComments] = useState<DiscussionCommentResponse[]>(
    [],
  );

  const [comment, setComment] = useState<string>();

  const getDiscussionComments = (id: number) => {
    dispatch(
      Main.getPaginatedPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_COMMENT(id),
        previousPage: 0,
        clear: true,
      }),
    );
  };

  useEffect(() => {
    if (discussion) {
      getDiscussionComments(discussion?.id);
    }
  }, [discussion]);

  useEffect(() => {
    dispatch(
      Main.getPageArrayData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION(Number(contentID)),
        clear: true,
      }),
    );

    if (comments.pageState === AppState.LOADED) {
      setPageComments(comments.data);
      setComment("");
    }
  }, [comments]);

  const submit = (e: any) => {
    e.preventDefault();

    if (comment!.length <= 0) {
      return;
    }

    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_COMMENT(
          Number(discussion?.id),
        ),
        req: { comment },
        append: "start",
        refresh: false,
      }),
    );
  };

  const reportPost = (e: any) => {
    e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_REPORT(
          Number(discussion?.id),
        ),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  const deletePost = (e: any) => {
    e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_DELETE(
          Number(discussion?.id),
        ),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  const reportComment = (id: any): void | undefined => {
    // e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_COMMENT_REPORT(Number(id)),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  const deleteComment = (id: any): void | undefined => {
    // e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_COMMENT_DELETE(Number(id)),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  const upvoteComment = (id: any): void | undefined => {
    // e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_COMMENT_UPVOTE(Number(id)),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  const downvoteComment = (id: any): void | undefined => {
    // e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION_COMMENTS,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_COMMENT_DOWNVOTE(
          Number(id),
        ),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  const conditions: string[] = ["minutes", "minute"];

  const mainTimeLabel =
    discussion &&
    (conditions.some((el) =>
      moment(new Date(discussion.created_at))
        .startOf("minute")
        .fromNow()
        .includes(el),
    )
      ? moment(new Date(discussion.created_at)).startOf("minute").fromNow()
      : moment(new Date(discussion.created_at)).format("ll"));

  const replyTimeLabel = (createdAt: string) =>
    conditions.some((el) =>
      moment(new Date(createdAt)).startOf("minute").fromNow().includes(el),
    )
      ? moment(new Date(createdAt)).startOf("minute").fromNow()
      : moment(new Date(createdAt)).format("ll");

  return (
    <>
      <Modal
        show={!!discussion}
        onHide={() => {
          setDiscussion(null!);
          setPageComments([]);
        }}
        centered
        scrollable
        dialogClassName="discussion-replies-modal"
        contentClassName="discussion-replies-modal__content"
        size="lg"
      >
        <Modal.Header className="discussion-replies-modal__header border-0 pb-0" />
        <Modal.Body className="discussion-replies-modal__body">
          <div className="discussion-replies-modal__scroll discussion-replies-modal__main-card">
            {discussion && (
              <div className="">
                <div className="discussion-replies-modal__post-header">
                  <div className="discussion-replies-modal__author">
                    <img
                      className="discussion-replies-modal__avatar discuss-profile"
                      src={discussion.user.image_url}
                      alt={discussion.user.name}
                    />
                    <span className="discussion-replies-modal__name">
                      {discussion.user.name}
                    </span>
                  </div>
                  <DropdownButton
                    variant="transparent"
                    title={
                      <i
                        className="las la-ellipsis-v discussion-replies-modal__kebab"
                        aria-hidden
                      />
                    }
                    className="discussion-replies-modal__dropdown"
                  >
                    {user?.id != discussion.user.id && (
                      <Dropdown.Item eventKey="report" onClick={reportPost}>
                        Report
                      </Dropdown.Item>
                    )}
                    {user?.id === discussion.user.id && (
                      <Dropdown.Item eventKey="delete" onClick={deletePost}>
                        Delete
                      </Dropdown.Item>
                    )}
                  </DropdownButton>
                </div>
                <p className="discussion-replies-modal__post-body">
                  {discussion.post}
                </p>
                <div className="discussion-replies-modal__post-footer">
                  <span className="discussion-replies-modal__post-stat">
                    <i className="bi bi-chat" aria-hidden />
                    <span>{discussion.total_comments}</span>
                  </span>
                  <span className="discussion-replies-modal__post-time">
                    {mainTimeLabel}
                  </span>
                </div>
              </div>
            )}

            {pageComments.length === 0 &&
            comments.pageState === AppState.LOADING ? (
              <div className="discussion-replies-modal__loading text-center py-4">
                <Spinner animation="border" size="sm" className="me-1" />
              </div>
            ) : (
              <div
                className="discussion-replies-modal__replies"
                style={{
                  marginTop: "50px",
                  borderTop: "1px solid #e0e0e0",
                  paddingTop: "40px",
                }}
              >
                {pageComments.map((replies) => (
                  <div
                    key={replies.id}
                    className="discussion-replies-modal__reply"
                  >
                    <div className="discussion-replies-modal__reply-inner">
                      <div className="discussion-replies-modal__post-header">
                        <div
                          className="discussion-replies-modal__author"
                          style={{ position: "relative" }}
                        >
                          <div
                            style={{
                              height: "50px",
                              position: "absolute",
                              border: "1px solid #e0e0e0",
                              borderEndStartRadius: "20px",
                              width: "40px",
                              left: -19,
                              bottom: 19,
                              borderBottom: "none",
                              borderRight: "none",
                              borderTop: "none",
                            }}
                          ></div>
                          <img
                            className="discussion-replies-modal__avatar discussion-replies-modal__avatar--sm discuss-profile"
                            src={replies.user.image_url}
                            alt={replies.user.name}
                          />
                          <span className="discussion-replies-modal__name discussion-replies-modal__name--sm">
                            {replies.user.name}
                          </span>
                        </div>
                        <DropdownButton
                          variant="transparent"
                          title={
                            <i
                              className="las la-ellipsis-v discussion-replies-modal__kebab"
                              aria-hidden
                            />
                          }
                          className="discussion-replies-modal__dropdown"
                        >
                          {user?.id != replies.user.id && (
                            <Dropdown.Item
                              eventKey="report"
                              onClick={() => reportComment(replies.id)}
                            >
                              Report
                            </Dropdown.Item>
                          )}
                          {user?.id === replies.user.id && (
                            <Dropdown.Item
                              eventKey="del"
                              onClick={() => deleteComment(replies.id)}
                            >
                              Delete
                            </Dropdown.Item>
                          )}
                        </DropdownButton>
                      </div>
                      <p className="discussion-replies-modal__reply-body">
                        {replies.comment}
                      </p>
                      <div className="discussion-replies-modal__reply-footer">
                        <div className="discussion-replies-modal__interactions">
                          {user?.id != replies.user.id && (
                            <>
                              <button
                                type="button"
                                className="discussion-replies-modal__vote discussion-replies-modal__vote--up"
                                onClick={() => upvoteComment(replies.id)}
                                aria-label="Upvote"
                              >
                                <i
                                  className="bi bi-hand-thumbs-up"
                                  aria-hidden
                                />
                                <span className="discussion-replies-modal__vote-count">
                                  0
                                </span>
                              </button>
                              <button
                                type="button"
                                className="discussion-replies-modal__vote discussion-replies-modal__vote--down"
                                onClick={() => downvoteComment(replies.id)}
                                aria-label="Downvote"
                              >
                                <i
                                  className="bi bi-hand-thumbs-down"
                                  aria-hidden
                                />
                                <span className="discussion-replies-modal__vote-count">
                                  0
                                </span>
                              </button>
                            </>
                          )}
                          <span className="discussion-replies-modal__reply-thread">
                            <i className="bi bi-chat" aria-hidden />
                            <span>{replies.total_comments ?? 0}</span>
                          </span>
                        </div>
                        <span className="discussion-replies-modal__reply-time">
                          {replyTimeLabel(replies.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="discussion-replies-modal__composer">
            <form onSubmit={submit}>
              <div className="row align-items-center gx-2 position-relative">
                <div className="col">
                  <textarea
                    className="form-control discussion-replies-modal__textarea"
                    name="post"
                    id="discussion-reply-comment"
                    placeholder="Add a reply"
                    onChange={(e) => setComment(e.target.value)}
                    required
                    disabled={comments.pageState === AppState.LOADING}
                    value={comment ?? ""}
                    rows={1}
                  />
                </div>
                <div className="col-auto d-flex align-items-center justify-content-center">
                  <button
                    type="submit"
                    className="btn btn-link p-0 border-0 discussion-replies-modal__send"
                    aria-label="Send reply"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="rgba(218, 55, 50, 1)"
                      className="bi bi-send"
                      viewBox="0 0 16 16"
                      aria-hidden
                    >
                      <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DiscussionReplies;
